/**
 * API 呼び出しの失敗を、利用者の次の行動が変わる単位に分ける判定。
 *
 * BFF は失敗の意味をコードに写す（`apps/api/spec-console` の oRPC ハンドラが唯一の写像元）。
 * web 側で意味のあるコードだけを特別扱いすることで、深いリンクの行き止まり
 * （`NotFoundState`）と通信やサーバーの障害（`ErrorState`）を利用者に区別させる。
 *
 * 判定を各画面に散らさないのは、`instanceof ORPCError` の判定漏れが「削除済みの ID を開くと
 * 障害画面が出る」という誤った案内として表面化し、型では捕まらないためである。
 *
 * 再試行の可否もここで決める。失敗の意味を読む場所と、その失敗をもう一度試すかを決める場所が
 * 離れていると、「未存在なのに再試行する」ような取り合わせが誰にも気づかれずに残る。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business API 呼び出しの失敗が未存在由来かを判定し、再試行の可否を決める。
 * @graph-connects none
 */
import { ORPCError } from '@orpc/client';

/**
 * 未存在（NOT_FOUND / 404）による失敗かを判定する。
 *
 * `isUnauthenticatedError` と同じく、oRPC のコードと HTTP status の両方を見る。BFF より
 * 手前（Edge Router Worker など）が返す 404 も同じ意味だからである。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business API 呼び出しの失敗が未存在由来（NOT_FOUND / 404）かを判定する。
 * @graph-connects none
 */
export function isNotFoundError(error: unknown): boolean {
  if (!(error instanceof ORPCError)) return false;
  return error.code === 'NOT_FOUND' || error.status === 404;
}

/**
 * 呼び出し側の誤り（4xx）による失敗かを判定する。
 *
 * 未存在も入力不正も、同じ入力で投げ直せば同じ答えが返る。再試行に意味があるのは通信の
 * 揺らぎとサーバー側の一時障害（5xx）だけである。
 *
 * @param error 判定する失敗
 * @returns 4xx なら true
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business API 呼び出しの失敗が呼び出し側の誤り（4xx）かを判定する。
 * @graph-connects none
 */
function isClientError(error: unknown): boolean {
  if (!(error instanceof ORPCError)) return false;
  return error.status >= 400 && error.status < 500;
}

/**
 * 取得の再試行可否を決める。
 *
 * **4xx は再試行しない。** 同じ入力に同じ答えが返るので試行が無駄になる、では済まない。
 * TanStack Query の既定（`networkMode: 'online'`）では、再試行に入った取得は接続状態の判定が
 * 揺れた瞬間に `paused` へ落ち、**そのまま永久に取得中のまま留まる**。失敗が確定しないので
 * 画面は未存在にも失敗にも倒れず、読み込み表示のまま固まる。
 *
 * この画面で 4xx は例外的な事故ではなく、**常設の経路で必ず通る**。PJ モードは変更前と変更後の
 * 2 面を取るが、PJ が新規に作った成果物にはマスター側の行が無く、変更前の読取は 404 に決まって
 * いる。同じ画面でもマスターに在る成果物は変更前が 200 で返るので、再試行の可否を運任せにすると
 * 対象ごとに挙動が割れる。
 *
 * @param maxRetries 5xx・通信障害のときに許す再試行の回数
 * @returns TanStack Query へ渡す再試行判定
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 取得の失敗が再試行に値するか（4xx でないか）を決める。
 * @graph-connects isClientError [calls] 呼び出し側の誤りかの判定
 */
export function shouldRetryQuery(
  maxRetries: number,
): (failureCount: number, error: unknown) => boolean {
  return (failureCount, error) => !isClientError(error) && failureCount < maxRetries;
}
